import React, { useState, useEffect } from "react";
import { Typography, Grid2 } from "@mui/material";
import Divider from "@mui/material/Divider";
import AppButton from "../../components/CommonReusableComponent/AppButton/AppButton";
import MUILoader from "../../components/MUILoader/MUILoader";
import MUITableSearch from "../../components/MUITable/MUITableSearch";
// components
import AreaMasterDataTable from "./components/AreaMasterDataTable";
import CreateNewOrUpdateAreaModal from "./components/CreateOrUpdateAreaModal";
import DeleteAreaConfirmationModal from "./components/DeleteAreaConfirmationModal";
import { areaMasterColumnsConfig } from "./components/AreaMasterDataTable.config";
import { handleOutSideDataTableSearch } from "../../common/MuiTableFunctions.config";
import { useSnackbar } from "../../contexts/Snackbar.context";
import { useBackdrop } from "../../contexts/BackDrop.context";
import {
  APP_MESSAGE,
  ERROR_MESSAGE,
  SNACKBAR_VARIANT,
} from "../../config/Messages.config";
import {
  deleteArea,
  getAllBranches,
  getAllDepartmentListByBranchId,
  getAreasByBranchAndDept,
} from "../../helpers/api-functions-helper";
import { getUserRoleAndScreens } from "../../services/authServices/auth.services";
import { getDataForAllOption, getPageTitle, setRequiredDataForDropdown, sortDataAlphabetically } from "../../common/CommonFunctions";
import AppInputField from "../../components/CommonReusableComponent/AppInputField/AppInputField";
import AreaMasterDataTable_V2 from "./components/AreaMasterDataTable_V2";
import { ROLES } from "../../config/Roles.config";
import AppAutoCompleteField from "../../components/CommonReusableComponent/AppAutoCompleteField/AppAutoCompleteField";

const AreaMaster = () => {
  const documentTitle = getPageTitle() && getPageTitle();
  document.title = documentTitle
    ? `${documentTitle} | Zing Auditor`
    : "Zing Auditor";
  // const authUser = JSON.parse(
  //   localStorage.getItem(process.env.REACT_APP_DEV_AUTH_USER)
  // );
  const { showSnackbar } = useSnackbar();
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const { accountId, assignedBranches, role, assignedDepartments } =
    getUserRoleAndScreens();
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isAreaGettingFetched, setIsAreaGettingFetched] = useState(false);
  const [tableDataInstance, setTableDataInstance] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [toggleCreateOrUpdateModal, setToggleCreateOrUpdateModal] =
    useState(false);
  const [toggleDeleteModal, setToggleDeleteModal] = useState(false);
  const [isDeleteClicked, setIsDeleteClicked] = useState(false);
  const [modalDataState, setModalDataState] = useState(null);

  // Store branch & dept
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState(null);

  // search functionality
  const [searchText, setSearchText] = useState("");

  // All Object for selection
  const allObjWithNameId = [{ name: "All", id: "All" }];
  // const allObjWithLabelValue = [{ label: "All", value: "All" }];

  const is_SuperAdmin =
    role && role.toLowerCase() === ROLES.SUPERADMIN ? true : false;

  // Fetch all active branches
  const fetchAllBranches = () => {
    const hasMultipleBranches = assignedBranches?.length > 1;
    const hasBranches = assignedBranches?.length > 0;

    const dataSet = is_SuperAdmin && hasMultipleBranches
      ? [...allObjWithNameId, ...assignedBranches]
      : assignedBranches;
    setRequiredDataForDropdown(setBranches, dataSet, "name", "id", ["isActive"]);

    const defaultPlantId =
      is_SuperAdmin && hasMultipleBranches
        ? "All"
        : hasBranches
        ? assignedBranches[0].id
        : null;
    setBranchId(defaultPlantId);
  };

  // Fetch all departments under branch
  const fetchAllDepartmentsOfBranch = () => {
    if (!is_SuperAdmin) {
      const dataSet =
        assignedDepartments?.length > 1
          ? [...allObjWithNameId, ...assignedDepartments]
          : assignedDepartments;
      const sortedData = sortDataAlphabetically(dataSet, "name");
      setRequiredDataForDropdown(setDepartments, sortedData, "name", "id");
      setDepartmentId(
        assignedDepartments?.length > 1
          ? "All"
          : assignedDepartments?.length > 0
          ? assignedDepartments[0].id
          : null
      );
    } else {
      const payload = {
        branchIds: getDataForAllOption("branch", branchId, branches),
        accountId: accountId,
      };
      showBackdrop();
      getAllDepartmentListByBranchId(payload)
        .then((response) => {
          if (response && response.data) {
            const { statusCode, data } = response.data;
            if (statusCode && statusCode === 200) {
              if (data && data?.length > 0) {
                const dataSet =
                  data?.length > 1 ? [...allObjWithNameId, ...data] : data;
                const sortedData = sortDataAlphabetically(dataSet, "name");
                setRequiredDataForDropdown(
                  setDepartments,
                  sortedData,
                  "name",
                  "id"
                );
                setDepartmentId(
                  sortedData?.length > 1
                    ? "All"
                    : sortedData?.length > 0
                    ? sortedData[0].id
                    : null
                );
              } else {
                setDepartments([]);
              }
            } else {
              setDepartments([]);
            }
          }
        })
        .catch((error) => {
          console.log("Error: ", error);
          showSnackbar(
            ERROR_MESSAGE.FAILED_TO_FETCH_DATA,
            SNACKBAR_VARIANT.ERROR
          );
        })
        .finally(() => {
          hideBackdrop();
        });
    }
  };

  // Fetch all areas under departments and branch
  const fetchAreaByBranchAndDept = () => {
    const payload = {
      accountId: accountId,
      branchIds: getDataForAllOption("branch", branchId, branches),
      departmentIds: getDataForAllOption(
        "department",
        departmentId,
        departments
      ),
    };
    setIsAreaGettingFetched(true);
    getAreasByBranchAndDept(payload)
      .then((response) => {
        if (response && response.data) {
          const { statusCode, data, message } = response.data;
          if (statusCode && statusCode === 200) {
            setTableDataInstance(data);
            setTableData(data);
          } else {
            showSnackbar(
              data || message || ERROR_MESSAGE.FAILED_TO_FETCH_DATA,
              SNACKBAR_VARIANT.ERROR
            );
          }
        }
      })
      .catch((error) => {
        console.log("Error: ", error);
        showSnackbar(
          ERROR_MESSAGE.FAILED_TO_FETCH_DATA,
          SNACKBAR_VARIANT.ERROR
        );
      })
      .finally(() => {
        setIsAreaGettingFetched(false);
      });
  };

  const deleteExistingArea = () => {
    setIsDeleteClicked(true);
    deleteArea(modalDataState?.id, accountId)
      .then((res) => {
        if (res && res.data) {
          // console.log(res.data);
          const { statusCode, success } = res.data;
          if (!success && statusCode !== 200) {
            showSnackbar(
              APP_MESSAGE.AREA_MASTER.DELETE.FAILED,
              SNACKBAR_VARIANT.ERROR
            );
          } else {
            showSnackbar(
              APP_MESSAGE.AREA_MASTER.DELETE.SUCCESS,
              SNACKBAR_VARIANT.SUCCESS
            );
            handleToggleModal("Delete");
            setModalDataState(null);
            fetchAreaByBranchAndDept();
          }
        }
      })
      .catch((error) => {
        console.log("Error: ", error);
        showSnackbar(
          APP_MESSAGE.PLANT_MASTER.DELETE.FAILED,
          SNACKBAR_VARIANT.ERROR
        );
      })
      .finally(() => {
        setIsDeleteClicked(false);
      });
  };

  // Function used toggle modal on their types
  const handleToggleModal = (modalType) => {
    if (modalType === "Create/Update") {
      setToggleCreateOrUpdateModal((e) => !e);
    } else {
      setToggleDeleteModal((e) => !e);
    }
  };

  const getRowDataOnClickOfAction = (actionName, data) => {
    if (actionName === "Update Area") {
      setModalDataState(data);
      handleToggleModal("Create/Update");
    } else if (actionName === "Create Area") {
      handleToggleModal("Create/Update");
    } else {
      setModalDataState(data);
      handleToggleModal("Delete Area");
    }
  };

  // To handle change branch & department
  const handleChangeBranchAndDept = (event) => {
    const { value, name } = event?.target;
    if (name === "branch") {
      setDepartmentId(null);
      setTableData([]);
      setTableDataInstance([]);
      setBranchId(value);
    } else {
      setDepartmentId(value);
    }
  };

  useEffect(() => {
    // Runs only when branchId changes
    if (branchId) {
      fetchAllDepartmentsOfBranch();
    }
  }, [branchId]);

  useEffect(() => {
    // Runs when either branchId or departmentId changes
    if (branchId && departmentId) {
      fetchAreaByBranchAndDept();
    }
  }, [branchId, departmentId]);

  useEffect(() => {
    if (navigator.onLine) {
      fetchAllBranches();
    } else {
      showSnackbar(ERROR_MESSAGE.INTERNET_ERROR, SNACKBAR_VARIANT.ERROR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Outside Table search functionality
  useEffect(() => {
    if (searchText === "") {
      // Reset to original data if search text is cleared
      setTableData(tableDataInstance);
    } else {
      const filteredData_ = handleOutSideDataTableSearch(
        searchText,
        tableDataInstance
      );
      setTableData(filteredData_);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <React.Fragment>
      {toggleCreateOrUpdateModal ? (
        <CreateNewOrUpdateAreaModal
          Open={toggleCreateOrUpdateModal}
          Title={modalDataState ? "Update Existing Area" : "Create New Area"}
          DialogData={modalDataState}
          SetDialogData={setModalDataState}
          ToggleDialogBox={handleToggleModal}
          AllAreaNames={
            tableDataInstance[0] &&
            tableDataInstance.map((branch) => branch.name.toLowerCase())
          }
          FetchAreaByBranchAndDept={fetchAreaByBranchAndDept}
          ListOfAllBranches={branches}
          CurrentBranchId={branchId}
          CurrentDepartmentId={departmentId}
        />
      ) : null}
      {toggleDeleteModal ? (
        <DeleteAreaConfirmationModal
          ModalState={toggleDeleteModal}
          SetModalState={setModalDataState}
          ToggleFunction={handleToggleModal}
          LoaderState={isDeleteClicked}
          OnSubmit={deleteExistingArea}
        />
      ) : null}
      <div className="page-content">
        {isPageLoading && !isAreaGettingFetched ? (
          <MUILoader />
        ) : (
          <div>
            <Grid2
              container
              className="d-flex align-items-center my-1"
              spacing={2}
            >
              <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 4 }} className="py-1">
                <Typography className="inter-medium-500">
                  {documentTitle}
                </Typography>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 4, md: 3, lg: 3 }} className="py-1">
                {/* <AppInputField
                  IsSelect={true}
                  Name={"branch"}
                  Id={"branch"}
                  Value={branchId}
                  SelectOptions={branches || []}
                  Label={"Select branch"}
                  OnChange={handleChangeBranchAndDept}
                /> */}
                <AppAutoCompleteField
                  IsMultiple={false}
                  Id={"branch"}
                  TextInputName={"branch"}
                  Value={branchId}
                  Options={branches || []}
                  TextInputLabel={"Select branch"}
                  TextInputPlaceholder={"Select branch"}
                  HandleOnChange={(event, value) => {
                    const eve_ = {
                      target: {
                        name: "branch",
                        value: value?.value,
                      },
                    };
                    handleChangeBranchAndDept(eve_);
                  }}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 4, md: 3, lg: 3 }} className="py-1">
                {/* <AppInputField
                  IsSelect={true}
                  Name={"department"}
                  Id={"department"}
                  Value={departmentId}
                  SelectOptions={departments || []}
                  Label={"Select department"}
                  OnChange={handleChangeBranchAndDept}
                /> */}
                <AppAutoCompleteField
                  IsMultiple={false}
                  Id={"department"}
                  TextInputName={"department"}
                  Value={departmentId}
                  Options={departments || []}
                  Disabled={!branchId || branchId === "All"}
                  TextInputLabel={"Select department"}
                  TextInputPlaceholder={"Select department"}
                  HandleOnChange={(event, value) => {
                    const eve_ = {
                      target: {
                        name: "department",
                        value: value?.value,
                      },
                    };
                    handleChangeBranchAndDept(eve_);
                  }}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 3, md: 2, lg: 2 }} className="py-1">
                <AppButton OnPress={() => handleToggleModal("Create/Update")}>
                  Create New Area
                </AppButton>
              </Grid2>
            </Grid2>

            <Divider
              style={{ color: "GrayText", border: "0.02rem solid GrayText" }}
              className="mt-2"
            />

            {isAreaGettingFetched ? (
              <MUILoader />
            ) : (
              <div>
                <Grid2 container className="my-2" spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
                    <MUITableSearch
                      CancelSearch={() => {
                        setSearchText("");
                      }}
                      HandleSearch={(e) => setSearchText(e.target.value)}
                      SearchText={searchText}
                    />
                  </Grid2>
                </Grid2>
                <Grid2 container className="my-2">
                  <Grid2 size={{ xs: 12 }}>
                    {/* <AreaMasterDataTable
                      Columns={areaMasterColumnsConfig}
                      RowData={tableData || []}
                      IsOutSideSearch={true}
                      DenseTable={true}
                      GetRowDataOnClickOfEditIcon={(actionName, data) => {
                        getRowDataOnClickOfAction(actionName, data);
                      }}
                      RowsToDisplay={10}
                      OptionsForRowsPerPage={[5, 10, 15, 25, 50]}
                    /> */}
                    <AreaMasterDataTable_V2
                      Rows={tableData || []}
                      GetRowDataOnClickOfAction={getRowDataOnClickOfAction}
                    />
                  </Grid2>
                </Grid2>
              </div>
            )}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default AreaMaster;
